import { GameState, CardDef, PlayerSide } from '../types.js';
import { getPlayerState, getInstance, getEffectivePower } from '../engine/GameState.js';
import { getConfig } from '../config.js';
import { createCardElement, updateCardElement } from './CardView.js';
import { LOG_CARD_LINK_RE } from '../shared/log-markers.js';

/** Toggle a fixed set of state classes on a card according to a
 *  truth-spec. Companion to keyed reconciliation in `renderBoard`:
 *  `classList.toggle(name, bool)` is a no-op when the class is already
 *  in the desired state, so reused card nodes don't fire CSS
 *  transitions unless the class actually changed.
 *
 *  Used only for renderer-owned classes (attacker, blocker, staged-
 *  attacker, united-primary, united-joiner). Interaction-owned classes
 *  (.playable, .support-source/target, etc.) are handled by their
 *  respective `updateInteractionUI` / `applyHoverHighlight` passes in
 *  Renderer.ts. */
function applyCardClasses(el: HTMLElement, spec: Record<string, boolean>): void {
  for (const cls in spec) el.classList.toggle(cls, spec[cls]);
}

export interface RenderOptions {
  stagedAttackers: Map<string, string[]>;
  maxHandSize: number;
}

const LOG_COLLAPSED_KEY = 'cardverse-log-collapsed';

/** Returns the user's saved log-collapsed preference, or `null` if
 *  they've never toggled. Caller falls back to a sensible default
 *  (collapse on small portrait screens) when the value is null. */
function loadLogCollapsed(): boolean | null {
  try {
    const v = localStorage.getItem(LOG_COLLAPSED_KEY);
    if (v === '1') return true;
    if (v === '0') return false;
    return null;
  } catch {
    return null;
  }
}

function saveLogCollapsed(collapsed: boolean): void {
  try {
    localStorage.setItem(LOG_COLLAPSED_KEY, collapsed ? '1' : '0');
  } catch { /* ignore */ }
}

export function createBoardDOM(): HTMLElement {
  const board = document.createElement('div');
  board.className = 'game-board';

  board.innerHTML = `
    <div class="player-info opponent" data-side="opponent">
      <span class="player-label">Opponent</span>
      <div class="hp-bar-container">
        <div class="hp-bar" style="width: 100%"></div>
        <span class="hp-text"></span>
      </div>
      <div class="spirit-display">✦ <span class="spirit-value">0</span></div>
      <span class="deck-count">Deck: <span class="deck-value">0</span></span>
    </div>
    <div class="hand-area opponent" data-side="opponent"></div>
    <div class="ring-area opponent" data-side="opponent"></div>
    <div class="battle-line" data-drop-zone="center"></div>
    <div class="ring-area player" data-side="player"></div>
    <aside class="task-list" aria-label="Available actions"></aside>
    <div class="hand-area player" data-side="player"></div>
    <div class="player-info player" data-side="player">
      <span class="player-label">You</span>
      <div class="hp-bar-container">
        <div class="hp-bar" style="width: 100%"></div>
        <span class="hp-text"></span>
      </div>
      <div class="spirit-display">✦ <span class="spirit-value">0</span></div>
      <span class="deck-count">Deck: <span class="deck-value">0</span></span>
    </div>
    <div class="phase-bar">
      <div class="phase-indicator"></div>
      <span class="turn-label"></span>
      <span class="clock-display" title="Play-clock">⏱<span class="clock-value">1</span></span>
      <button class="action-button" id="main-action-btn" style="display:none"></button>
      <button class="action-button secondary" id="secondary-action-btn" style="display:none"></button>
    </div>
    <div class="game-log">
      <button class="game-log-toggle" aria-label="Toggle log">Log</button>
      <div class="log-entries"></div>
    </div>
  `;

  const gameLog = board.querySelector('.game-log') as HTMLElement;
  // Saved preference wins over the mobile-portrait default — if the
  // player has ever explicitly toggled, that choice persists across
  // matches. Only fall back to the portrait/narrow-screen default when
  // no preference has ever been set.
  const savedLogCollapsed = loadLogCollapsed();
  if (savedLogCollapsed !== null) {
    gameLog.classList.toggle('collapsed', savedLogCollapsed);
  } else if (window.matchMedia('(orientation: portrait) and (max-width: 900px)').matches) {
    gameLog.classList.add('collapsed');
  }
  board.querySelector('.game-log-toggle')!.addEventListener('click', () => {
    const collapsed = gameLog.classList.toggle('collapsed');
    saveLogCollapsed(collapsed);
  });

  // Initialize ring slots
  const config = getConfig();
  for (const side of ['player', 'opponent'] as PlayerSide[]) {
    const ringArea = board.querySelector(`.ring-area[data-side="${side}"]`)!;
    for (let i = 0; i < config.ringSlots; i++) {
      const slot = document.createElement('div');
      slot.className = 'ring-slot empty';
      slot.dataset.slotIndex = String(i);
      slot.dataset.side = side;
      ringArea.appendChild(slot);
    }
  }

  // Phase pips. Only two surface to the UI:
  //   main    — player's main phase
  //   combat  — either 'attack' (your turn) or 'counter' (opponent's turn);
  //             the text and highlight are set each render based on context.
  // The engine still has activate/draw/end/cleanup internally, but they
  // auto-advance and aren't interesting to players.
  const phaseIndicator = board.querySelector('.phase-indicator')!;
  for (const phase of ['main', 'combat']) {
    const pip = document.createElement('span');
    pip.className = 'phase-pip';
    pip.dataset.phase = phase;
    pip.textContent = phase;
    phaseIndicator.appendChild(pip);
  }

  // Recompute hand-card overlap on window resize. `applyHandOverlap`
  // otherwise only fires from renderBoard (i.e. on dispatched state
  // changes) — without this listener, resizing the window to a
  // narrower viewport leaves the cards at their previous natural
  // spacing and the rightmost ones spill off behind `overflow-x:
  // hidden`. Debounced so a drag-resize doesn't recompute every
  // frame; 120ms feels responsive without thrashing.
  let resizeTimer: ReturnType<typeof setTimeout> | null = null;
  window.addEventListener('resize', () => {
    if (resizeTimer !== null) clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      resizeTimer = null;
      board.querySelectorAll<HTMLElement>('.hand-area').forEach(applyHandOverlap);
    }, 120);
  });

  return board;
}

export function renderBoard(
  board: HTMLElement,
  state: GameState,
  catalog: Map<string, CardDef>,
  options: RenderOptions = { stagedAttackers: new Map(), maxHandSize: Infinity },
): void {
  const stagedAttackerIds = new Set<string>();
  const unitedPrimaries = new Set<string>();
  const unitedJoiners = new Set<string>();
  // joiner instance id → { primaryId, joinerIndex } where joinerIndex is
  // 1 for the first joiner, 2 for the second. The second positioning pass
  // uses joinerIndex to fan multiple joiners out so a triple attack reads
  // as three distinct cards instead of two stacked behind the primary.
  const joinerInfo = new Map<string, { primaryId: string; joinerIndex: number }>();
  // primaryId → summed effective power of the joiners behind it. Used to
  // render a "+N" overlay on the primary so the player can see the total
  // power they're committing to a united attack at a glance.
  const unitedBonusPower = new Map<string, number>();
  // Defensive: drop any member whose card has been KO'd since staging.
  // Renderer.render() also filters the staging map at the top-level
  // dispatch path, but this guards refresh paths and any future intermediate
  // state where a card was destroyed (zone='discard') without the staging
  // map being recomputed yet.
  const isLive = (id: string): boolean => {
    const inst = state.cardInstances.get(id);
    return !!inst && inst.zone !== 'discard';
  };
  for (const [primaryId, members] of options.stagedAttackers) {
    if (!isLive(primaryId)) continue;
    const live = members.filter(isLive);
    for (const id of live) stagedAttackerIds.add(id);
    if (live.length >= 2) {
      unitedPrimaries.add(primaryId);
      let bonus = 0;
      for (let i = 1; i < live.length; i++) {
        unitedJoiners.add(live[i]);
        joinerInfo.set(live[i], { primaryId, joinerIndex: i });
        bonus += getEffectivePower(state, live[i], catalog);
      }
      unitedBonusPower.set(primaryId, bonus);
    }
  }
  // After DECLARE_ATTACKERS the local staging map is cleared, but the
  // engine's combatState.groups carries the same structure. Pull joiner →
  // primary mapping from there too so the visual pose persists through
  // attack/counter for either side. Same KO filter applies here.
  if (state.combatState) {
    for (const group of state.combatState.groups) {
      const live = group.attackerIds.filter(isLive);
      if (live.length < 2) continue;
      const primaryId = live[0];
      unitedPrimaries.add(primaryId);
      let bonus = 0;
      for (let i = 1; i < live.length; i++) {
        unitedJoiners.add(live[i]);
        joinerInfo.set(live[i], { primaryId, joinerIndex: i });
        bonus += getEffectivePower(state, live[i], catalog);
      }
      unitedBonusPower.set(primaryId, bonus);
    }
  }
  // Engine-declared blockers — characters currently assigned to intercept an
  // attacker group. Used to apply the .blocker forward-lean visual.
  const blockerIds = new Set<string>();
  if (state.combatState) {
    for (const [, blockerId] of state.combatState.blockerAssignments) {
      blockerIds.add(blockerId);
    }
  }

  for (const side of ['player', 'opponent'] as PlayerSide[]) {
    const ps = getPlayerState(state, side);

    // HP
    const info = board.querySelector(`.player-info[data-side="${side}"]`)!;
    const hpBar = info.querySelector('.hp-bar') as HTMLElement;
    const hpText = info.querySelector('.hp-text')!;
    const displayHp = Math.max(0, ps.hp);
    const hpPct = Math.max(0, (displayHp / state.maxHp) * 100);
    hpBar.style.width = `${hpPct}%`;
    hpBar.classList.toggle('low', hpPct < 30);
    hpText.textContent = `${displayHp} / ${state.maxHp}`;

    // Spirit
    const spiritVal = info.querySelector('.spirit-value')!;
    spiritVal.textContent = String(ps.spirit);

    // Deck count
    const deckVal = info.querySelector('.deck-value')!;
    deckVal.textContent = String(ps.deck.length);

    // Hand — keyed reconciliation by instanceId. Existing card nodes
    // are reused (CSS-transition state preserved); new cards are
    // inserted in the right position; obsolete cards are removed.
    // Skips the drag placeholder (no `data-instance-id`) so an in-
    // flight drag isn't disturbed by mid-drag renders.
    const handArea = board.querySelector(`.hand-area[data-side="${side}"]`) as HTMLElement;
    reconcileHand(handArea, ps.hand, side, state, catalog);
    applyHandOverlap(handArea);

    // Ring — slot containers are long-lived; only their interiors
    // reconcile. Each card node persists across re-renders when its
    // instanceId stays in the same slot.
    const ringArea = board.querySelector(`.ring-area[data-side="${side}"]`)!;
    const slots = ringArea.querySelectorAll('.ring-slot');
    slots.forEach((slotEl, i) => {
      const htmlSlot = slotEl as HTMLElement;
      // Drop the drag drop-target marker; it's reapplied per-render by
      // DragController hover logic, not part of board state.
      htmlSlot.classList.remove('valid-target');
      reconcileRingSlot(
        htmlSlot,
        ps.ring[i].instanceId,
        side,
        state,
        catalog,
        stagedAttackerIds,
        blockerIds,
        unitedPrimaries,
        unitedJoiners,
        unitedBonusPower,
        ps.attackLine,
      );
    });
  }

  // Position pass: pull each joiner's card visually over its primary's
  // slot. Done after both rings are populated because the offset depends
  // on the final slot layout. Inline transforms override the CSS-class
  // transform from .united-joiner; the CSS still supplies the glow and
  // border styling.
  //
  // Runs synchronously now AND scheduled again via rAF as a safety net.
  // The first run is normally sufficient; the rAF re-run guards against
  // a corner case where ring-slot offsets aren't yet laid out (some
  // browsers can have offsetLeft return stale values immediately after a
  // class change that triggers reflow). The CSS `.united-joiner` rule
  // also provides a no-JS fallback pose so a missed pass doesn't leave
  // the joiner looking like a normal ring card.
  if (joinerInfo.size > 0) {
    positionJoiners(board, state, joinerInfo);
    requestAnimationFrame(() => positionJoiners(board, state, joinerInfo));
  }

  // Trash icon — drop target for DISCARD_CARD when over hand limit. Lives to
  // the right of the player's ring slots (not in the hand area) so it's
  // reachable even when the hand is overflowing.
  const playerRingArea = board.querySelector('.ring-area[data-side="player"]') as HTMLElement;
  const existingTrash = playerRingArea.querySelector('.hand-trash');
  const playerPs = getPlayerState(state, 'player');
  const showTrash =
    state.currentPlayer === 'player' &&
    playerPs.hand.length > options.maxHandSize;
  if (showTrash) {
    if (!existingTrash) {
      const trash = document.createElement('div');
      trash.className = 'hand-trash';
      trash.textContent = '🗑';
      trash.title = 'Drag a card here to discard';
      playerRingArea.appendChild(trash);
    }
  } else if (existingTrash) {
    existingTrash.remove();
  }

  // Phase bar. The 'combat' pip stands in for both the engine's 'attack' and
  // 'counter' phases; its label flips to "block" when the opponent is the
  // attacker so the defender knows they're the one making decisions.
  const inCombat = state.phase === 'attack' || state.phase === 'counter';
  const combatLabel = state.currentPlayer === 'player' ? 'attack' : 'block';
  const pips = board.querySelectorAll('.phase-pip');
  pips.forEach(pip => {
    const htmlPip = pip as HTMLElement;
    const phase = htmlPip.dataset.phase;
    if (phase === 'combat') {
      htmlPip.textContent = combatLabel;
      htmlPip.classList.toggle('active', inCombat);
    } else {
      htmlPip.classList.toggle('active', phase === state.phase);
    }
  });

  // Turn label
  const turnLabel = board.querySelector('.turn-label')!;
  turnLabel.textContent = `Turn ${state.turnNumber} — ${state.currentPlayer === 'player' ? 'Your' : "Opponent's"} turn`;

  // Clock indicator (shared play-clock)
  const clockValue = board.querySelector('.clock-value')!;
  clockValue.textContent = String(state.clock);

  // Battle line
  const battleLine = board.querySelector('.battle-line')!;
  battleLine.classList.toggle('active', state.phase === 'attack' || state.phase === 'counter');

  // Log. Each entry may embed `{{card:id|name}}` markers (see
  // shared/log-markers.ts) — convert them to clickable spans so the
  // player can pull up a detail view on any card the log references.
  // The card-id lives on the span as a data attribute; a delegated
  // click handler in Renderer.ts opens the modal.
  refreshGameLog(board, state);
}

/** Re-paint just the game-log section of the board DOM from
 *  state.log. Extracted from the full render so voice-line routing
 *  (which pushes entries AFTER the main render call) can update the
 *  log without rebuilding every card. Safe to call anytime once the
 *  board is mounted. */
export function refreshGameLog(board: HTMLElement, state: GameState): void {
  const logEntries = board.querySelector('.log-entries') as HTMLElement | null;
  if (!logEntries) return;
  // Render the full log — the container is scrollable and auto-scrolls
  // to the newest entry below, so a long history stays reviewable
  // without truncating older lines. Even a long match produces at most
  // a few hundred entries, well within cheap innerHTML territory.
  logEntries.innerHTML = state.log.map(entry =>
    `<div class="log-entry">${formatLogEntry(entry)}</div>`
  ).join('');
  logEntries.scrollTop = logEntries.scrollHeight;
}

/** Set inline transforms on united-attack joiner cards so they visually
 *  stack on their primary's slot. Extracted from `renderBoard` because
 *  it runs both synchronously and again on the next animation frame —
 *  the rAF re-run guards against any path where the synchronous run's
 *  offsetLeft measurements lag the actual layout. The function is
 *  idempotent: re-running with the same state produces the same
 *  transforms. */
function positionJoiners(
  board: HTMLElement,
  state: GameState,
  joinerInfo: Map<string, { primaryId: string; joinerIndex: number }>,
): void {
  for (const side of ['player', 'opponent'] as PlayerSide[]) {
    const ringArea = board.querySelector(`.ring-area[data-side="${side}"]`);
    if (!ringArea) continue;
    const ps = getPlayerState(state, side);
    const slotEls = ringArea.querySelectorAll('.ring-slot');
    for (let i = 0; i < ps.ring.length; i++) {
      const id = ps.ring[i].instanceId;
      if (!id) continue;
      const info = joinerInfo.get(id);
      if (!info) continue;
      const primaryIdx = ps.ring.findIndex(r => r.instanceId === info.primaryId);
      if (primaryIdx < 0) continue;
      const joinerSlot = slotEls[i] as HTMLElement | undefined;
      const primarySlot = slotEls[primaryIdx] as HTMLElement | undefined;
      if (!joinerSlot || !primarySlot) continue;
      const cardEl = joinerSlot.querySelector('.card') as HTMLElement | null;
      if (!cardEl) continue;
      // Cascade joiners diagonally so a triple attack shows three
      // distinct cards. joinerIndex is 1-based: the 1st joiner sits
      // just behind the primary, the 2nd sits further down-right
      // behind the 1st.
      const cascade = info.joinerIndex - 1;
      const dx = primarySlot.offsetLeft - joinerSlot.offsetLeft + 14 + cascade * 22;
      // Push the joiner ~30% lower than the primary's translate so the
      // joiner's bottom strip (name + power + spirit) peeks out from
      // behind the primary instead of being entirely hidden by it. The
      // primary translates -25% on player, +25% on opponent — so the
      // first joiner's translate is +5% / +55% respectively. Each
      // additional joiner steps down a further 10%.
      const baseDy = side === 'player' ? 5 : 55;
      const dyPercent = baseDy + cascade * 10;
      cardEl.style.transform = `translate(${dx}px, ${dyPercent}%) scale(0.85)`;
      // Stack joiners back-to-front so each subsequent joiner sits
      // behind the one before it. united-primary is z-index 3, joiner
      // base is z-index 2 — bumping later joiners down to 1 keeps them
      // strictly behind the first joiner.
      if (cascade > 0) cardEl.style.zIndex = String(2 - cascade);
    }
  }
}

/** Mark a freshly-created card so its initial state-class additions
 *  (.playable, .support-source, etc., applied by Renderer's interaction
 *  pass after renderBoard returns) don't animate from the default
 *  border/box-shadow. The transition is restored on the next animation
 *  frame, after the current render task has fully committed — so any
 *  subsequent class toggle animates normally. Without this guard, every
 *  newly-drawn card would visibly fade in its highlight. */
function suppressInitialTransition(el: HTMLElement): void {
  el.style.transition = 'none';
  requestAnimationFrame(() => {
    if (el.style.transition === 'none') el.style.transition = '';
  });
}

/** Hand reconciliation. Walks `instanceIds` in order, reusing existing
 *  card nodes by `data-instance-id`, inserting new ones at the right
 *  position, and removing nodes whose instance is no longer in hand.
 *  Skips non-card siblings (e.g. the DragController placeholder), so an
 *  in-flight drag stays intact through mid-drag re-renders. */
function reconcileHand(
  handArea: HTMLElement,
  instanceIds: string[],
  side: PlayerSide,
  state: GameState,
  catalog: Map<string, CardDef>,
): void {
  const isOpponent = side === 'opponent';

  // Build an instanceId → existing card element map. The selector
  // filters to .card elements that carry a data-instance-id so we
  // skip the drag placeholder and any future non-card siblings.
  const existing = new Map<string, HTMLElement>();
  handArea.querySelectorAll<HTMLElement>(':scope > .card[data-instance-id]').forEach(el => {
    existing.set(el.dataset.instanceId!, el);
  });

  // Forward pass: walk the desired order, place each card at the
  // expected position via `insertBefore`. Browsers no-op an
  // `insertBefore` whose node is already at the requested position,
  // so we can call unconditionally without measuring DOM moves.
  let anchor = handArea.firstChild;
  // If a drag placeholder is the first child, the first reconciled card
  // should land *after* it. Skip non-card placeholders when picking the
  // initial anchor.
  while (anchor && !(anchor instanceof HTMLElement && anchor.classList.contains('card') && anchor.dataset.instanceId)) {
    anchor = anchor.nextSibling;
  }
  for (const id of instanceIds) {
    const inst = getInstance(state, id);
    const def = catalog.get(inst.cardId)!;
    let cardEl = existing.get(id);
    if (cardEl) {
      // Reuse — update mutable state, then move into position if needed.
      updateCardElement(cardEl, def, inst);
      existing.delete(id);
    } else {
      // Create — fresh DOM node for a card that wasn't here last frame.
      cardEl = createCardElement(def, inst, isOpponent);
      cardEl.dataset.instanceId = id;
      suppressInitialTransition(cardEl);
    }
    if (cardEl !== anchor) {
      handArea.insertBefore(cardEl, anchor);
    } else {
      anchor = anchor.nextSibling;
      // Skip over any non-card siblings (placeholder) when advancing.
      while (anchor && !(anchor instanceof HTMLElement && anchor.classList.contains('card') && anchor.dataset.instanceId)) {
        anchor = anchor.nextSibling;
      }
    }
  }

  // Remove any leftover nodes — instances that left the hand this render.
  for (const el of existing.values()) el.remove();
}

/** Ring-slot reconciliation. Either keeps and updates the existing card
 *  in the slot (same instanceId), swaps it for a different card, or
 *  empties the slot. Also applies renderer-owned state classes
 *  (.attacker / .blocker / .staged-attacker / .united-primary /
 *  .united-joiner) and reconciles the `+N` united-bonus overlay. */
function reconcileRingSlot(
  slotEl: HTMLElement,
  desiredId: string | null,
  side: PlayerSide,
  state: GameState,
  catalog: Map<string, CardDef>,
  stagedAttackerIds: Set<string>,
  blockerIds: Set<string>,
  unitedPrimaries: Set<string>,
  unitedJoiners: Set<string>,
  unitedBonusPower: Map<string, number>,
  attackLine: string[],
): void {
  const existing = slotEl.querySelector<HTMLElement>(':scope > .card');

  if (!desiredId) {
    if (existing) existing.remove();
    slotEl.classList.add('empty');
    return;
  }

  const inst = getInstance(state, desiredId);
  const def = catalog.get(inst.cardId)!;
  const ep = getEffectivePower(state, desiredId, catalog);

  let cardEl: HTMLElement;
  if (existing && existing.dataset.instanceId === desiredId) {
    cardEl = existing;
    updateCardElement(cardEl, def, inst, ep);
  } else {
    if (existing) existing.remove();
    cardEl = createCardElement(def, inst, false, ep, true);
    cardEl.dataset.instanceId = desiredId;
    slotEl.appendChild(cardEl);
    suppressInitialTransition(cardEl);
  }
  slotEl.classList.remove('empty');

  // Capture the prior joiner state BEFORE applyCardClasses overwrites
  // it — used below to detect when a card stops being a joiner so we
  // can clear its inline transform / zIndex.
  const wasJoiner = cardEl.classList.contains('united-joiner');

  const isPrimary = unitedPrimaries.has(desiredId);
  const isJoiner = unitedJoiners.has(desiredId);
  applyCardClasses(cardEl, {
    attacker: attackLine.includes(desiredId),
    blocker: blockerIds.has(desiredId),
    // Local staging only renders on the player's own side.
    'staged-attacker': side === 'player' && stagedAttackerIds.has(desiredId),
    'united-primary': isPrimary,
    'united-joiner': isJoiner,
  });

  // United-bonus overlay (+N under the power badge). Reconciled here
  // because the bonus value depends on staging state, not on the card
  // instance itself — `updateCardElement` deliberately stays
  // instance-only.
  const bonus = isPrimary ? (unitedBonusPower.get(desiredId) ?? 0) : 0;
  let bonusEl = cardEl.querySelector<HTMLElement>(':scope > .card-power-bonus');
  if (bonus > 0) {
    const text = `+${bonus}`;
    if (bonusEl) {
      if (bonusEl.textContent !== text) bonusEl.textContent = text;
    } else {
      bonusEl = document.createElement('div');
      bonusEl.className = 'card-power-bonus';
      bonusEl.textContent = text;
      cardEl.appendChild(bonusEl);
    }
  } else if (bonusEl) {
    bonusEl.remove();
  }

  // When a card stops being a joiner, drop the inline transform /
  // zIndex the joiner-positioning pass had stamped on it. Scoped to
  // the exact transition out of joiner state so we don't clobber other
  // owners of inline transform — notably DragController, which sets
  // `transform: translate(...)` on the dragged card mid-drop and
  // relies on it surviving any reconcile that fires before
  // `endSession` clears the drag styles.
  if (wasJoiner && !isJoiner) {
    cardEl.style.transform = '';
    cardEl.style.zIndex = '';
  }
}

/** Escape an entry for HTML output, then convert `{{card:id|name}}`
 *  markers into clickable spans. We escape first and then rewrite the
 *  (now-escaped) markers, because the marker characters (`{}|:`) all
 *  pass through `escapeHtml` unchanged and stay matchable by
 *  `LOG_CARD_LINK_RE`. */
function formatLogEntry(entry: string): string {
  const escaped = escapeHtml(entry);
  return escaped.replace(LOG_CARD_LINK_RE, (_match, id, name) =>
    `<span class="log-card-link" data-card-id="${escapeAttr(id)}">${name}</span>`,
  );
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function escapeAttr(s: string): string {
  return escapeHtml(s);
}

/** Make hand cards overlap when their natural total width exceeds the
 *  visible hand-area. Large hands would otherwise overflow off the right
 *  edge — invisible on mobile and impossible to play / discard.
 *
 *  With keyed reconciliation, hand card nodes persist across renders.
 *  Each card retains its last-render `margin-left`, so we don't need to
 *  reset margins before measuring: the previously-applied overlap is
 *  precisely what makes the current hand fit, and the measurement
 *  reflects that. We only write a new margin when it differs from the
 *  current value, which keeps `transition: all` on `.card` from firing
 *  on every render.
 *
 *  When the hand shrinks back below the overflow threshold we still
 *  need to clear an existing negative margin. That's the one path where
 *  a transition would animate over 200ms — we suppress it with a
 *  one-frame `transition: none` so the un-squeeze snaps cleanly. */
function applyHandOverlap(handArea: HTMLElement): void {
  // Filter to real cards only — the DragController's drag placeholder
  // is a sibling without a data-instance-id and shouldn't count toward
  // the overflow math.
  const cards = Array.from(
    handArea.querySelectorAll<HTMLElement>(':scope > .card[data-instance-id]'),
  );
  if (cards.length === 0) return;

  const style = getComputedStyle(handArea);
  const padL = parseFloat(style.paddingLeft) || 0;
  const padR = parseFloat(style.paddingRight) || 0;
  const gap = parseFloat(style.columnGap || style.gap || '0') || 0;
  const available = handArea.clientWidth - padL - padR;
  const cardW = cards[0].getBoundingClientRect().width;
  if (cardW <= 0 || available <= 0) return;

  const naturalTotal = cards.length * cardW + (cards.length - 1) * gap;
  let targetMargin = '';
  if (naturalTotal > available && cards.length > 1) {
    // Per-pair shrinkage to make the row fit. The negative margin-left
    // on each subsequent card moves it left by this amount, so the
    // row's total width drops by (N-1) × reduction to land at
    // `available`. Cap so cards never overlap by more than 80% of
    // their width (keep ≥20% visible).
    const reduction = (naturalTotal - available) / (cards.length - 1);
    const maxReduction = gap + cardW * 0.8;
    targetMargin = `${-Math.min(reduction, maxReduction)}px`;
  }

  // Apply the margin only where it differs. Track cards that would
  // un-squeeze (margin → '') so we can suppress the transition for
  // that specific change.
  const needsSnap: HTMLElement[] = [];
  for (let i = 1; i < cards.length; i++) {
    const card = cards[i];
    const current = card.style.marginLeft;
    if (current === targetMargin) continue;
    if (targetMargin === '' && current !== '') needsSnap.push(card);
    card.style.marginLeft = targetMargin;
  }
  if (needsSnap.length > 0) {
    for (const c of needsSnap) c.style.transition = 'none';
    void handArea.offsetHeight;
    requestAnimationFrame(() => {
      for (const c of needsSnap) c.style.transition = '';
    });
  }
}
